/**
 * RunDetailBody - shared body for displaying run details, statistics, errors, and failed records.
 * Rendered inside page chrome on the Run Details page. Polls for updates while the run is active.
 */

import Box from "@cloudscape-design/components/box"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import { useMemo } from "react"
import type { RunDetailBodyProps, RuleEntry, Severity } from "./types"
import ConformanceErrorsTable from "./ConformanceErrorsTable"
import FailedRecordsTable from "./FailedRecordsTable"
import ProcessingStatistics from "./ProcessingStatistics"
import RunOverviewGrid from "./RunOverviewGrid"
import SystemErrorAlert from "./SystemErrorAlert"
import { useRunDetail } from "./useRunDetail"

/**
 * Fetches and displays full run details including metadata, processing stats, conformance errors,
 * and quarantined records. Polls for updates while the run is active.
 */
export default function RunDetailBody({ studyId, runId }: RunDetailBodyProps) {
  const { run, quarantineRecords, isLoading, isLoadingQuarantine, error } = useRunDetail(
    studyId,
    runId,
  )

  // Build a shared rule numbering map from conformance errors so both the table and
  // the failed records section use consistent numbers.
  const ruleMap = useMemo(() => {
    const map = new Map<string, RuleEntry>()
    if (!run) return map

    run.conformanceErrors?.forEach((err) => {
      if (!map.has(err.rule)) {
        map.set(err.rule, { num: map.size + 1, severity: err.stage as Severity })
      }
    })
    run.childRuns?.forEach((child) => {
      child.conformanceErrors?.forEach((err) => {
        if (!map.has(err.rule)) {
          map.set(err.rule, { num: map.size + 1, severity: err.stage as Severity })
        }
      })
    })

    // Also pick up rules from quarantine records that aren't in conformance errors
    for (const rec of quarantineRecords) {
      if (Array.isArray(rec.DataQualityRulesFail)) {
        for (const rule of rec.DataQualityRulesFail as string[]) {
          if (!map.has(rule)) {
            map.set(rule, { num: map.size + 1, severity: "major" })
          }
        }
      }
      const fallback = rec._failure_rule || rec._failure_reason
      if (fallback && typeof fallback === "string" && !map.has(fallback)) {
        map.set(fallback, { num: map.size + 1, severity: "major" })
      }
    }

    return map
  }, [run, quarantineRecords])

  return (
    <SpaceBetween size="l">
      {isLoading && (
        <Box textAlign="center" padding="l">
          <SpaceBetween size="s" alignItems="center" direction="vertical">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading run details...</Box>
          </SpaceBetween>
        </Box>
      )}

      {error && !run && <StatusIndicator type="error">{error.message}</StatusIndicator>}

      {run && (
        <>
          <RunOverviewGrid run={run} />

          {run.statistics && (
            <ProcessingStatistics statistics={run.statistics} isDryRun={run.dryRun} />
          )}

          {run.errorMessage && <SystemErrorAlert errorMessage={run.errorMessage} />}

          <ConformanceErrorsTable run={run} ruleMap={ruleMap} />

          {isLoadingQuarantine && (
            <Box textAlign="center" padding="s">
              <Spinner />
            </Box>
          )}

          {!isLoadingQuarantine && quarantineRecords.length > 0 && (
            <FailedRecordsTable
              records={quarantineRecords}
              ruleMap={ruleMap}
              runColumns={run.quarantineSummary?.dataColumns ?? []}
            />
          )}
        </>
      )}
    </SpaceBetween>
  )
}
